import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import axiosClient from "../../services/apiClient";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as PieTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import AlertTrendsCard from "./AlertTrendsCard";
import AlertHeatmapCard from "./AlertHeatmapCard";
import SensorTrendCard from "./SensorTrendCard";
import TrendBadge from "../../components/ui/TrendBadge";
import { computeSensorTrend } from "./trendUtils";
import ProvisioningQRModal from "../../components/ui/ProvisioningQRModal";
import { useInsightsCtx } from "./InsightsLayout";
import {
  getSensorAlarms,
  getSensorSessions,
  getSensorPresence,
  getSensorConnectivity,
  getSensorStateChanges,
  getPCOStateChanges,
  getBootInfo,
  getSensors,
  groupAlertsByType,
  listAlertTransitions,
  countAlarmShutoffs,
  deriveStoveControl,
} from "./unitEventsUtils";
import Spinner from "../../components/ui/Spinner";
import ErrorState from "../../components/ui/ErrorState";
import StatusDot, { type Tone } from "../../components/ui/StatusDot";
import Button from "../../components/ui/Button";
import SensorScheduleCard from "../../components/ui/SensorScheduleCard";
import StoveStateBadge from "../../components/ui/StoveStateBadge";
import { reducePcoState } from "../../services/cookingState";
import {
  getSchedule,
  emptyWeekSchedule,
  isWithinUnlockWindow,
} from "../../services/schedule";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

const ALERT_LABEL: Record<string, string> = {
  isTooHot: "Dangerous temperature",
  isUnattended: "Unattended cooking",
  isImmediateDanger: "Immediate danger",
};

function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Friendly relative time for the Recent Activity rows. Falls back to the
 *  absolute date for anything older than a week. */
function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "Just now";
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "Yesterday";
  if (day < 7) return `${day} days ago`;
  return formatAbsolute(ts);
}

function MetaItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-ink-3">{label}</div>
      <div
        className={`text-sm font-medium text-ink truncate ${mono ? "font-mono" : ""}`}
        title={value}
      >
        {value}
      </div>
    </div>
  );
}

const ACTIVITY_TONE: Record<string, string> = {
  neutral: "bg-black/[0.05] text-ink-2",
  success: "bg-alert-success/10 text-alert-success",
  warning: "bg-alert-warning/10 text-alert-warning",
  info: "bg-alert-info/10 text-alert-info",
};

function ActivityRow({
  icon,
  tone = "neutral",
  label,
  value,
  sub,
}: {
  icon: ReactNode;
  tone?: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-black/5 last:border-0">
      <div
        className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${ACTIVITY_TONE[tone]}`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-ink-3">{label}</div>
        <div className="text-sm font-medium text-ink truncate" title={value}>
          {value}
        </div>
        {sub && sub !== value ? (
          <div className="text-xs text-ink-3 truncate" title={sub}>
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: number | string;
  tone?: "critical" | "warning" | "info" | "muted" | "success" | "fire";
  title?: string;
}) {
  const colorClass = {
    critical: "text-alert-critical",
    warning: "text-alert-warning",
    info: "text-alert-info",
    muted: "text-ink-3",
    success: "text-alert-success",
    fire: "text-alert-fire",
  }[tone || "muted"];
  return (
    <div
      className="flex-1 min-w-[120px] rounded-card border border-white/10 bg-white/5 p-4 text-center flex flex-col"
      title={title}
    >
      <div className="text-xs uppercase tracking-wide text-ink-3 min-h-[2.6em] flex items-start justify-center">
        {label}
      </div>
      <div className={`text-3xl font-bold mt-2 ${colorClass}`}>{value}</div>
    </div>
  );
}

export default function SensorDetailPage() {
  const { buildingId = "", sensorId: rawSensorId = "" } = useParams();
  const sensorId = decodeURIComponent(rawSensorId);
  const navigate = useNavigate();
  const [qrOpen, setQrOpen] = useState(false);
  const [pendingMsg, setPendingMsg] = useState<string | null>(null);
  const { buildings, startDate, endDate, tempUnit } = useInsightsCtx();

  const building = buildings.find((b) => b.buildingId === buildingId);

  // Sensors list for this building (cached if we just came from the sensors page).
  const {
    data: sensorListResp,
    isPending: sensorListLoading,
    error: sensorListError,
  } = useQuery({
    queryKey: ["sensors", buildingId],
    queryFn: async () => await getSensors(buildingId),
    enabled: !!buildingId,
    staleTime: 60_000,
  });
  const sensorList: { name: string; displayName?: string }[] =
    sensorListResp?.data ?? [];
  const sensor = sensorList.find((s) => s.name === sensorId);

  // Alerts (shared cache key with the aggregates hook + Level 1 table).
  const { data: alertsResp, isPending: alertsLoading } = useQuery({
    queryKey: ["alerts-agg", sensorId],
    queryFn: async () => await getSensorAlarms(sensorId, 5000),
    enabled: !!sensorId,
  });
  const alertRecords = useMemo(
    () => alertsResp?.data ?? [],
    [alertsResp]
  );

  // Trends chart history uses the same `alerts-agg` cache (now 5000 records).
  const TREND_HISTORY_PAGE_SIZE = 5000;
  const historyRecords = alertRecords;
  const trendHistoryFetching = alertsLoading;

  // Sessions: pull a window so we can show "last session" and compute the
  // presence ratio off the same payload. Server-filters when picker dates are set.
  const { data: sessionsResp, isPending: lastSessionLoading } = useQuery({
    queryKey: ["recent-sessions", sensorId, startDate, endDate],
    queryFn: async () =>
      await getSensorSessions(sensorId, 50, startDate, endDate),
    enabled: !!sensorId,
  });
  const sessions = useMemo(
    () => sessionsResp?.data ?? [],
    [sessionsResp]
  );

  // Larger session pull powering the trends chart's year/all-time views.
  const SESSIONS_HISTORY_PAGE_SIZE = 1000;
  const { data: sessionsHistoryResp } = useQuery({
    queryKey: ["sessions-history", sensorId],
    queryFn: async () =>
      await getSensorSessions(
        sensorId,
        SESSIONS_HISTORY_PAGE_SIZE,
        null,
        null
      ),
    enabled: !!sensorId,
    staleTime: 60_000,
  });
  const historySessions = useMemo(
    () => sessionsHistoryResp?.data ?? [],
    [sessionsHistoryResp]
  );

  const lastSession = sessions[0] ?? null;

  const { data: presenceResp } = useQuery({
    queryKey: ["last-presence", sensorId],
    queryFn: async () => await getSensorPresence(sensorId, 1),
    enabled: !!sensorId,
  });
  const lastPresenceTs = useMemo(() => {
    const row = presenceResp?.data?.[0];
    if (!row?.datetime) return null;
    const ts = Date.parse(String(row.datetime).replace(" ", "T"));
    return Number.isNaN(ts) ? null : ts;
  }, [presenceResp]);

  // Connectivity / state for the header.
  const { data: connResp } = useQuery({
    queryKey: ["connectivity", sensorId],
    queryFn: async () => await getSensorConnectivity(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: stateResp } = useQuery({
    queryKey: ["statechanges", sensorId],
    queryFn: async () => await getSensorStateChanges(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: pcoResp } = useQuery({
    queryKey: ["currentPCOstatechanges", sensorId],
    queryFn: async () => await getPCOStateChanges(sensorId, 1),
    enabled: !!sensorId,
  });
  const { data: bootResp } = useQuery({
    queryKey: ["bootinfo", sensorId],
    queryFn: async () => await getBootInfo(sensorId, 1),
    enabled: !!sensorId,
  });

  const isOnline = connResp?.data?.[0]?.eventType === "connected";

  // Trend: last 4 weeks vs the prior 4 weeks (alarm + session history, already
  // fetched). Priority also factors in being offline.
  const trend = useMemo(
    () =>
      computeSensorTrend(alertRecords, historySessions, new Date(), {
        offlineNorm: isOnline ? 0 : 1,
      }),
    [alertRecords, historySessions, isOnline]
  );
  const pcoState = pcoResp?.data?.[0];
  const stateRow = stateResp?.data?.[0];
  const stoveOn =
    stateRow &&
    stateRow.currentStateEnum !== 6 &&
    stateRow.currentStateEnum !== 9;
  // Match the Sensors-page detection: a PCO is attached whenever the latest
  // PCO state-change record exists and reports a non-zero connected status.
  // (Mobile's `checkShutdown` uses the same rule.)
  const pcoAttached =
    !!pcoState && pcoState.currentUnitConnectedStatus !== 0;
  const pcoStateReduced = reducePcoState(pcoState);
  const pcoLocked =
    pcoState && !pcoState.currentIsStoveRemoteUnlocked && !stoveOn;
  const scsFwRaw = bootResp?.data?.[0]?.firmwareVersion;
  const scsFw = scsFwRaw ? String(scsFwRaw).split("#")[0].trim() : null;
  const pcoFw = pcoState
    ? `${pcoState.currentFWverMajor}.${pcoState.currentFWverMinor}.${pcoState.currentFWverPatch}`
    : null;

  // Schedule (shared cache key with SensorScheduleCard) lets us tell a
  // scheduler-driven lock apart from a manual remote shut-off.
  const { data: scheduleData } = useQuery({
    queryKey: ["schedule", sensorId],
    queryFn: async () =>
      (await getSchedule(sensorId)) ?? {
        schedule: emptyWeekSchedule(),
        schedulerActive: false,
      },
    enabled: !!sensorId && isOnline && pcoAttached,
    staleTime: 30_000,
    retry: false,
  });
  // True when the scheduler is on AND the schedule wants the stove locked now
  // (i.e. we're outside every unlock window), so the lock is scheduled, not manual.
  const lockedBySchedule =
    !!scheduleData?.schedulerActive &&
    !isWithinUnlockWindow(scheduleData.schedule, new Date());

  // Derived data, filtered by the page-level date range.
  const alertsByType = useMemo(
    () => groupAlertsByType(alertRecords, startDate, endDate),
    [alertRecords, startDate, endDate]
  );
  const shutoffCount = useMemo(
    () => countAlarmShutoffs(alertRecords, startDate, endDate),
    [alertRecords, startDate, endDate]
  );
  // "Last alert" should be truly last, independent of the picker window.
  const lastAlertEver = useMemo(
    () => listAlertTransitions(alertRecords, null, null)[0] ?? null,
    [alertRecords]
  );

  if (sensorListLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (sensorListError || !building) {
    return (
      <ErrorState
        title="Property not found"
        message="We couldn't load this property's sensors."
        onRetry={() => navigate("/insights")}
      />
    );
  }

  if (!sensor) {
    return (
      <ErrorState
        title="Sensor not found"
        message={`No sensor named "${sensorId}" in ${building.name}.`}
        onRetry={() => navigate(`/insights/properties/${buildingId}`)}
      />
    );
  }

  // Same Stove Control logic as the Sensors table; derives badge + action
  // from connectivity / PCO / stove / lock state.
  const ctrl = deriveStoveControl({
    Status: isOnline ? "Online" : "Offline",
    isPCOconnected: pcoAttached ? "YES" : "NO",
    stoveStatus: stoveOn ? "ON" : "OFF",
    IsStoveRemoteUnlocked: pcoLocked ? "LOCKED" : "UNLOCKED",
  });

  // When the stove is locked, distinguish a scheduled lock from a manual remote
  // shut-off. Falls back to the generic locked copy when we can't tell.
  const controlMessage =
    ctrl.action === "unlock" && lockedBySchedule
      ? "Cooktop is off on its schedule. Click Unlock to override and power it on at the appliance."
      : ctrl.tooltip;

  // Show an overlay while the command goes out, give the device a few seconds
  // to apply it, then reload so the user sees the fresh state.
  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const applyAndReload = async (path: string) => {
    setPendingMsg("Sending command, please wait…");
    try {
      await axiosClient.post(`sensors/${sensorId}/${path}`);
    } catch (e) {
      console.error("Cooktop command failed", e);
      setPendingMsg(null);
      window.alert("Failed to send command. Please try again.");
      return;
    }
    await sleep(5000);
    window.location.reload();
  };

  const handleShutoff = async () => {
    if (
      !window.confirm(
        `Disable and lock ${sensor.displayName || sensorId}?`
      )
    ) {
      return;
    }
    applyAndReload("shutdownstove");
  };

  const handleUnlock = async () => {
    if (!window.confirm(`Unlock ${sensor.displayName || sensorId}?`)) {
      return;
    }
    applyAndReload("unlockstove");
  };

  const handleLock = async () => {
    if (
      !window.confirm(
        `Lock ${sensor.displayName || sensorId}?`
      )
    ) {
      return;
    }
    applyAndReload("shutdownstove");
  };

  const onAction = () => {
    if (ctrl.action === "shutoff") handleShutoff();
    else if (ctrl.action === "unlock") handleUnlock();
    else if (ctrl.action === "lock") handleLock();
  };

  // Recent Activity values (relative primary line + absolute secondary).
  const lastSessionTs = (() => {
    const raw = lastSession?.dateStart;
    const ts = raw ? Date.parse(String(raw)) : NaN;
    return Number.isNaN(ts) ? null : ts;
  })();
  const cookingValue = lastSessionLoading
    ? "…"
    : lastSessionTs == null
      ? "No sessions recorded"
      : timeAgo(lastSessionTs);
  const presenceValue =
    lastPresenceTs == null ? "Not detected" : timeAgo(lastPresenceTs);
  const alertValue = lastAlertEver
    ? ALERT_LABEL[lastAlertEver.type]
    : "None recorded";
  const alertSub = lastAlertEver
    ? (() => {
        const rel = timeAgo(lastAlertEver.timestamp);
        const abs = formatAbsolute(lastAlertEver.timestamp);
        return rel === abs ? abs : `${rel} · ${abs}`;
      })()
    : undefined;

  return (
    <div className="space-y-6">
      {pendingMsg && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-card p-6 text-ink shadow-card flex items-center gap-3 max-w-md">
            <Spinner size={32} />
            <span className="text-sm">{pendingMsg}</span>
          </div>
        </div>
      )}
      {/* Top row: header (left) + recent activity (right) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-semibold leading-tight break-words">
                {sensor.displayName || sensorId}
              </h1>
              <div className="text-ink-2 mt-1 text-sm">{building.name}</div>
            </div>
            <Button
              variant="ghost"
              size="md"
              onClick={() => setQrOpen(true)}
              leadingIcon={<QrCode2Icon sx={{ fontSize: 22 }} />}
              className="!text-ink !border-black/15 hover:!bg-black/[0.04] shrink-0"
            >
              QR Code
            </Button>
          </div>

          {/* Combined state: connectivity + stove power live in one badge
              (stove power is only known when the sensor is online), shown
              alongside the safety trend. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4">
            <StoveStateBadge
              sensorName={sensorId}
              isOnline={isOnline}
              pcoState={pcoStateReduced}
              unit={tempUnit}
            />
            <span className="h-4 w-px bg-black/10" aria-hidden />
            <TrendBadge status={trend.status} />
          </div>

          {/* Dedicated stove-control panel. */}
          <div className="mt-4 rounded-control border border-black/10 bg-black/[0.02] p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-ink-3">
                Cooktop control
              </div>
              <div className="text-sm text-ink-2 mt-0.5">{controlMessage}</div>
            </div>
            {ctrl.action ? (
              <Button
                size="md"
                variant={ctrl.action === "shutoff" ? "danger" : "info"}
                leadingIcon={
                  ctrl.action === "unlock" ? (
                    <LockOpenIcon fontSize="small" />
                  ) : ctrl.action === "lock" ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    <PowerSettingsNewIcon fontSize="small" />
                  )
                }
                onClick={onAction}
                title={controlMessage}
                className="!rounded-control !border-black whitespace-nowrap shadow-sm shrink-0"
              >
                {ctrl.actionLabel}
              </Button>
            ) : (
              <StatusDot
                tone={ctrl.tone as Tone}
                label={ctrl.longLabel}
                className="shrink-0"
              />
            )}
          </div>

          <div className="mt-auto pt-5">
            <div className="border-t border-black/5 pt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              <MetaItem label="Sensor firmware" value={scsFw || "-"} />
              <MetaItem label="Power cutoff firmware" value={pcoFw || "-"} />
              <MetaItem label="Sensor ID" value={sensorId} mono />
            </div>
          </div>
        </div>

        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5 flex flex-col">
          <h2 className="text-xl font-semibold mb-2">Recent Activity</h2>
          <div className="flex flex-col">
            <ActivityRow
              icon={<RestaurantIcon sx={{ fontSize: 20 }} />}
              tone="info"
              label="Last cooking session"
              value={cookingValue}
              sub={lastSessionTs != null ? formatAbsolute(lastSessionTs) : undefined}
            />
            <ActivityRow
              icon={<PersonIcon sx={{ fontSize: 20 }} />}
              tone="success"
              label="Last presence detected"
              value={presenceValue}
              sub={lastPresenceTs != null ? formatAbsolute(lastPresenceTs) : undefined}
            />
            <ActivityRow
              icon={<WarningAmberIcon sx={{ fontSize: 20 }} />}
              tone="warning"
              label="Last alert"
              value={alertValue}
              sub={alertSub}
            />
          </div>
        </div>
      </div>

      <SensorTrendCard trend={trend} />

      {pcoAttached && <SensorScheduleCard sensorName={sensorId} />}

      {/* Bottom row: combined Alerts (tiles + pie) on the left, trends on the right */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
        <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Alerts</h2>
            {alertsLoading && (
              <span className="text-ink-3 text-sm">Loading…</span>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mb-6">
            <StatCard
              label="Unattended"
              value={alertsByType.isUnattended}
              tone="warning"
            />
            <StatCard
              label="Dangerous temperature"
              value={alertsByType.isTooHot}
              tone="fire"
            />
            <StatCard
              label="Immediate danger"
              value={alertsByType.isImmediateDanger}
              tone="critical"
            />
            <StatCard
              label="Safety shut offs"
              value={pcoAttached ? shutoffCount : "N/A"}
              tone="info"
              title={
                pcoAttached
                  ? "Times the cooktop was automatically shut off in response to a danger alert during this window."
                  : "This sensor has no power cutoff unit attached."
              }
            />
          </div>
          <div className="flex-1 min-h-0">
            {(() => {
              const pieData = [
                {
                  name: "Unattended",
                  value: alertsByType.isUnattended,
                  color: "#FB8C00",
                },
                {
                  name: "Dangerous temperature",
                  value: alertsByType.isTooHot,
                  color: "#E74C3C",
                },
                {
                  name: "Immediate danger",
                  value: alertsByType.isImmediateDanger,
                  color: "#B00020",
                },
              ].filter((d) => d.value > 0);
              const total = pieData.reduce((a, d) => a + d.value, 0);
              if (total === 0) {
                return (
                  <div className="text-ink-3 text-sm text-center py-12">
                    No alerts in this window.
                  </div>
                );
              }
              return (
                <ResponsiveContainer width="100%" height="100%" minHeight={224}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {pieData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <PieTooltip
                      formatter={(value: number, name: string) => [
                        `${value} (${Math.round((value / total) * 100)}%)`,
                        name,
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      wrapperStyle={{ fontSize: "12px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </div>

        <AlertTrendsCard
          records={alertRecords}
          historyRecords={historyRecords}
          historyLoading={trendHistoryFetching}
          historyCap={TREND_HISTORY_PAGE_SIZE}
          sessions={sessions}
          historySessions={historySessions}
          title="History"
        />
      </div>

      <AlertHeatmapCard
        records={alertRecords}
        historyRecords={historyRecords}
        sessions={sessions}
        historySessions={historySessions}
      />

      <ProvisioningQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        sensorName={sensorId}
        displayName={sensor.displayName}
      />
    </div>
  );
}
